// src/components/_dashboard/WidgetPanel.js
import React, { forwardRef } from 'react';
import { Box, Typography, Switch, IconButton, Tooltip } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DigitalWatch from './_widgets/DigitalWatch';
import AnalogWatch from './_widgets/AnalogWatch';
import ServiceQueueWidget from './_widgets/ServiceQueueWidget';
import DailyProgressWidget from './_widgets/DailyProgressWidget';
import ProductCategoryWidget from './_widgets/ProductCategoryWidget';
import DataSourceWidget from './_widgets/DataSourceWidget';
import UserInfoWidget from './_widgets/UserInfoWidget';
import ScreenshotWidget from './_widgets/ScreenshotWidget';
import EstimateStatusWidget from './_widgets/EstimateStatusWidget';
import SamsungServiceStatusWidget from './_widgets/SamsungServiceStatusWidget';

const widgets = [
    { id: 1, name: "Digital Watch", component: DigitalWatch },
    { id: 2, name: "Analog Watch", component: AnalogWatch },
    { id: 3, name: "Service Queue", component: ServiceQueueWidget },
    { id: 4, name: "Daily Progress", component: DailyProgressWidget },
    { id: 5, name: "Apple Product Categories", component: () => <ProductCategoryWidget brand="Apple" /> },
    { id: 6, name: "Samsung Product Categories", component: () => <ProductCategoryWidget brand="Samsung" /> },
    { id: 7, name: "Data Source", component: DataSourceWidget },
    { id: 8, name: "User Info", component: UserInfoWidget },
    { id: 9, name: "System Screenshot", component: ScreenshotWidget },
    { id: 10, name: "Estimate Status", component: EstimateStatusWidget },
    { id: 11, name: "Samsung LP Status", component: SamsungServiceStatusWidget }
];


const WidgetPanel = forwardRef(({ isOpen, onToggleWidget, activeWidgets = [], onResetLayout, currentZone }, ref) => {
    // Ensure activeWidgets is an array
    const safeActiveWidgets = Array.isArray(activeWidgets) ? activeWidgets : [];

    return (
        <Box
            ref={ref}
            sx={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: 300,
                height: '100vh',
                backgroundColor: '#333',
                color: 'white',
                overflowY: 'auto',
                '&::-webkit-scrollbar': { display: 'none' },
                msOverflowStyle: 'none',  // IE and Edge
                scrollbarWidth: 'none',   // Firefox
                transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.3s ease-in-out',
                padding: 2,
                zIndex: 1200,
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <Typography variant="h5">Widgets</Typography>
                <Tooltip title="Reset widget layout">
                    <IconButton 
                        onClick={onResetLayout}
                        sx={{ color: 'white' }}
                    >
                        <RestartAltIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            <Typography variant="body1" sx={{ marginBottom: 2 }}>
                Enable or disable widgets to customize your dashboard.
            </Typography>
            
            <Typography variant="body2" sx={{ marginBottom: 1, color: '#aaa' }}>
                Currently editing: Zone {currentZone}
            </Typography>

            <Box sx={{ marginTop: 2 }}>
                {widgets.map((widget) => {
                    const activeWidget = safeActiveWidgets.find(w => w.id === widget.id);
                    const isWidgetActive = !!activeWidget;
                    const isInCurrentZone = isWidgetActive && 
                        Array.isArray(activeWidget.zones) && 
                        activeWidget.zones.includes(currentZone);
                    
                    return (
                        <Box
                            key={widget.id}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 2,
                                opacity: isWidgetActive && !isInCurrentZone ? 0.5 : 1,
                                position: 'relative',
                            }}
                        >
                            <Typography variant="subtitle1">
                                {widget.name}
                                {activeWidget && Array.isArray(activeWidget.zones) && (
                                    <Typography component="span" sx={{ fontSize: '0.7rem', color: '#aaa', ml: 1 }}>
                                        {`Zone${activeWidget.zones.length > 1 ? 's' : ''}: ${activeWidget.zones.join(', ')}`}
                                    </Typography>
                                )}
                            </Typography>
                            <Switch
                                color="primary"
                                checked={isInCurrentZone}
                                onChange={(e) => onToggleWidget(widget, e.target.checked)}
                                disabled={isWidgetActive && !isInCurrentZone && activeWidget.zones.length >= 3}
                            />
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
});

export default WidgetPanel;
